import type { Diff } from "@tiptap-pro/extension-snapshot-compare";
import { normalizeTiptapUserId } from "../version-authoring";
import { DOVETAIL_VERSION_COMPARISON_USER_ID } from "./version-comparison-presentation";

const VERSION_COMPARISON_DELETION_SUGGESTION_TYPES = new Set([
  "delete",
  "replaceDeletion",
]);

export const getVersionComparisonDecorationDiffType = (
  diff: Pick<Diff, "attributes" | "diffContentType" | "type">
): Diff["type"] => {
  const suggestionId = diff.attributes?.suggestionId;
  const suggestionType = diff.attributes?.suggestionType;
  const isSuggestionDeletion = Boolean(
    typeof suggestionId === "string" &&
      suggestionId.trim() &&
      typeof suggestionType === "string" &&
      VERSION_COMPARISON_DELETION_SUGGESTION_TYPES.has(suggestionType)
  );

  return diff.type === "block-update" &&
    diff.diffContentType === "attribute-only" &&
    isSuggestionDeletion
    ? "block-delete"
    : diff.type;
};

const normalizeVersionComparisonReplacementAttribution = (diff: Diff): Diff => {
  const attributionType = diff.type.endsWith("-delete")
    ? "removed"
    : diff.type.endsWith("-insert")
      ? "added"
      : diff.attribution.type;

  return attributionType === diff.attribution.type
    ? diff
    : {
        ...diff,
        attribution: {
          ...diff.attribution,
          type: attributionType,
        },
      };
};

const DOVETAIL_AGENT_SUGGESTION_SOURCE = "dovetail-agent";

const getVersionComparisonSuggestionUserId = ({
  userId,
  userMetadata,
}: {
  userId: unknown;
  userMetadata: unknown;
}) => {
  const directUserId = typeof userId === "string" ? userId.trim() : "";
  const metadata =
    userMetadata && typeof userMetadata === "object"
      ? (userMetadata as Record<string, unknown>)
      : null;
  const isDovetailAgentSuggestion = Boolean(
    metadata &&
      (metadata.actor === DOVETAIL_AGENT_SUGGESTION_SOURCE ||
        metadata.source === DOVETAIL_AGENT_SUGGESTION_SOURCE ||
        typeof metadata.agentUserId === "string")
  );
  const onBehalfOfUserId =
    isDovetailAgentSuggestion && typeof metadata?.onBehalfOfUserId === "string"
      ? metadata.onBehalfOfUserId.trim()
      : "";
  const resolvedUserId = onBehalfOfUserId || directUserId;

  return resolvedUserId ? normalizeTiptapUserId(resolvedUserId) : null;
};

/**
 * Adapts native Snapshot Compare output that contains tracked suggestions to
 * the shape the editor paints without changing the comparison calculation.
 */
export const normalizeVersionComparisonDiffForRendering = (
  inputDiff: Diff
): Diff => {
  const diff = normalizeVersionComparisonReplacementAttribution(inputDiff);
  const suggestionUserIds = new Set<string>();
  let hasInlineSuggestionMark = false;
  const addSuggestionUserId = (suggestionUserId: string | null) => {
    if (suggestionUserId) {
      suggestionUserIds.add(suggestionUserId);
    }
  };

  diff.content.nodesBetween(0, diff.content.size, (node) => {
    if (
      typeof node.attrs.suggestionId === "string" &&
      node.attrs.suggestionId.trim()
    ) {
      addSuggestionUserId(
        getVersionComparisonSuggestionUserId({
          userId: node.attrs.suggestionUserId,
          userMetadata: node.attrs.suggestionUserMetadata,
        })
      );
    }

    node.marks
      .filter((mark) => mark.type.name === "suggestion")
      .forEach((suggestionMark) => {
        hasInlineSuggestionMark = true;
        addSuggestionUserId(
          getVersionComparisonSuggestionUserId({
            userId: suggestionMark.attrs.userId,
            userMetadata: suggestionMark.attrs.userMetadata,
          })
        );
      });
  });

  let content = diff.content;
  let from = diff.from;
  const isSuggestionInsertion =
    hasInlineSuggestionMark &&
    diff.diffContentType === "mark-only" &&
    diff.type.endsWith("-insert");

  if (isSuggestionInsertion) {
    while (
      content.childCount === 1 &&
      content.firstChild?.isBlock &&
      content.firstChild.content.size > 0
    ) {
      content = content.firstChild.content;
      from += 1;
    }
  }

  const suggestionUserId =
    suggestionUserIds.size === 1 ? Array.from(suggestionUserIds)[0] : null;
  const shouldUseSuggestionUserId =
    suggestionUserId &&
    (!diff.attribution.userId ||
      diff.attribution.userId === DOVETAIL_VERSION_COMPARISON_USER_ID);
  const renderingDiff: Diff = {
    ...diff,
    attribution: shouldUseSuggestionUserId
      ? { ...diff.attribution, userId: suggestionUserId }
      : diff.attribution,
    content,
    from,
    to: content !== diff.content ? from + content.size : diff.to,
  };

  if (content.childCount === 0) {
    return renderingDiff;
  }

  let containsOnlyInlineContent = true;
  content.forEach((node) => {
    if (!node.isInline) {
      containsOnlyInlineContent = false;
    }
  });

  if (!containsOnlyInlineContent) {
    return renderingDiff;
  }

  switch (renderingDiff.type) {
    case "block-insert":
      return { ...renderingDiff, type: "inline-insert" };
    case "block-delete":
      return { ...renderingDiff, type: "inline-delete" };
    case "block-update":
      return { ...renderingDiff, type: "inline-update" };
    default:
      return renderingDiff;
  }
};

/** Hides the redundant delete half of a pending inline suggestion replacement. */
export const shouldRenderVersionComparisonDiff = (
  diff: Diff,
  index: number,
  diffs: Diff[]
) => {
  if (!diff.type.endsWith("-delete")) {
    return true;
  }

  const insertedDiff = diffs[index + 1];
  const isMatchingInsertion =
    insertedDiff?.type.endsWith("-insert") && insertedDiff.from === diff.from;
  if (!isMatchingInsertion) {
    return true;
  }

  let isSuggestionInsertion = false;
  insertedDiff.content.nodesBetween(0, insertedDiff.content.size, (node) => {
    isSuggestionInsertion ||= node.marks.some(
      (mark) => mark.type.name === "suggestion"
    );
  });
  return !isSuggestionInsertion;
};
